/**
 * src/pages/WhereIsEverythingGoing/sections/TimeHorizonsSection.tsx
 * "Zoom out in time": TabExplorer / horizon selector with five horizons
 * Next 100 years, Next 10,000 years, Next billion years, Next 100 billion years, Beyond 10^14 years.
 * Each horizon carries 3–4 evidence-labelled statements and citation footnotes.
 */

import React from "react";
import { TabExplorer } from "@/components/originBeyond/TabExplorer";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { TIME_HORIZONS } from "../futureData";
import type { TabItem } from "@/lib/originBeyond/types";
import { Clock, Hourglass, Sparkles, Orbit, Compass } from "lucide-react";
export function TimeHorizonsSection() {
  const tabIcons = [Clock, Hourglass, Orbit, Sparkles, Compass];
  const tabs: TabItem[] = TIME_HORIZONS.map((horizon, idx) => {
    const Icon = tabIcons[idx] || Clock;
    return {
      id: horizon.id,
      label: horizon.label,
      icon: Icon,
      content: <div className="flex flex-col gap-5 w-full min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-border/40">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                Horizon Span
              </span>
              <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground mt-0.5">
                {horizon.label}{" "}
                <span className="text-muted-foreground font-mono text-sm font-normal">
                  ({horizon.span})
                </span>
              </h3>
            </div>
          </div>

          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">
            {horizon.summary}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2 min-w-0">
            {horizon.claims.map(claim => <div key={claim.id} className="ql-glass-strong p-4 sm:p-5 rounded-xl border border-border/60 flex flex-col justify-between gap-3 min-w-0">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <EvidenceBadge level={claim.level} compact />
                    {claim.refs && claim.refs.length > 0 && <span className="inline-flex items-center">
                        {claim.refs.map(r => <Footnote key={r} n={r} />)}
                      </span>}
                  </div>
                  <h4 className="font-serif text-sm sm:text-base font-semibold text-foreground tracking-wide leading-snug break-words">
                    {claim.title}
                  </h4>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans break-words">
                  {claim.body}
                </p>
              </div>)}
          </div>
        </div>
    };
  });
  return <section id="time-horizons" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="horizons-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Temporal Navigation Telemetry" title="Zoom Out in Time" description="Traverse logarithmic orders of magnitude—from immediate human horizons across technological disruption to the cosmic isolation of galaxy clusters." />

        <div className="w-full min-w-0 mt-6 sm:mt-8">
          <TabExplorer tabs={tabs} ariaLabel="Deep-Time Horizons Navigator" defaultId="h-100yr" />
        </div>
      </div>
    </section>;
}